import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/context/AuthContext";
import { JourneyProvider, useJourneys } from "@/context/JourneyContext";
import { trackTripCreated } from "@/lib/analytics";
import Layout from "./components/Layout";
import Index from "./pages/Index";
import Dashboard from "./pages/Dashboard";
import SharedTrip from "./pages/SharedTrip";
import Stats from "./pages/Stats";
import Trip from "./pages/Trip";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import NotFound from "./pages/NotFound";
import ConsentBanner from "./components/ConsentBanner";
import ConsentedAnalytics from "./components/ConsentedAnalytics";

const queryClient = new QueryClient();

const NewJourneyRedirect = () => {
  const { createEmptyJourney } = useJourneys();
  const navigate = useNavigate();

  useEffect(() => {
    const trip = createEmptyJourney();
    // /journey/new is reached from the Dashboard "Plan new trip" button or a
    // direct link (the landing page fires its own trip_created with source
    // "landing"). Count this as a dashboard-sourced creation.
    trackTripCreated("dashboard");
    navigate(`/journey/${trip.id}`, { replace: true });
    // Intentional empty deps: only run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider
      attribute="data-theme"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
    >
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
          <JourneyProvider>
            <Routes>
              <Route element={<Layout />}>
                <Route path="/" element={<Index />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/stats" element={<Stats />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
              </Route>
              <Route path="/trip/:id" element={<SharedTrip />} />
              <Route path="/journey/new" element={<NewJourneyRedirect />} />
              <Route path="/journey/:id" element={<Trip />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            <ConsentBanner />
            <ConsentedAnalytics />
          </JourneyProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
